// src/pages/CreateTag.js - AVEC GESTION DES ERREURS D'ABONNEMENT ET QUOTAS
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const CreateTag = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  
  // États pour l'erreur de quota
  const [quotaError, setQuotaError] = useState({
    show: false,
    type: "",
    type_label: "",
    message: "",
    utilises: 0,
    total: 0,
    restant: 0
  });
  
  const [formData, setFormData] = useState({
    type_tag: "QR",
    info_tag: "",
    statut_tag: "1",
    nfc_uid: ""
  });

  const [quotaInfoQr, setQuotaInfoQr] = useState(null);
  const [quotaInfoNfc, setQuotaInfoNfc] = useState(null);

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
        
        // Vérifier les permissions (seuls les gestionnaires peuvent créer des tags)
        if (String(normalizedData.profile).trim() !== "3") {
          setError("Accès refusé. Seuls les gestionnaires peuvent créer des tags.");
        }
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Quotas "tags QR"/"tags NFC" restants selon l'abonnement (affichés avant
  // même de soumettre — les deux à la fois puisque le type se choisit dans le
  // formulaire).
  useEffect(() => {
    if (!userInfo?.societe_id) return;
    const token = localStorage.getItem("access_token");
    axios.get(`${API_BASE_URL}/achat_quota/get_restant.php?id_societe=${userInfo.societe_id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (res.data?.success) {
          setQuotaInfoQr(res.data.quotas?.tag_qr || null);
          setQuotaInfoNfc(res.data.quotas?.tag_nfc || null);
        }
      })
      .catch(() => {});
  }, [userInfo]);

  // Validation en temps réel
  useEffect(() => {
    validateFields();
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur de quota
    if (quotaError.show) {
      setQuotaError({ show: false });
    }
  };

  const validateFields = () => {
    const errors = {};

    // Validation Info Tag
    if (!formData.info_tag.trim()) {
      errors.info_tag = "Les informations du tag sont requises";
    } else if (formData.info_tag.trim().length > 1000) {
      errors.info_tag = "Maximum 1000 caractères";
    } else if (formData.info_tag.trim().length < 2) {
      errors.info_tag = "Minimum 2 caractères";
    }

    // Validation pour NFC seulement
    if (formData.type_tag === "NFC") {
      if (!formData.nfc_uid.trim()) {
        errors.nfc_uid = "Le NFC UID est requis pour un tag de type NFC";
      } else if (formData.nfc_uid.trim().length > 64) {
        errors.nfc_uid = "Maximum 64 caractères pour un NFC UID";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation avant soumission
    if (!validateFields()) {
      setError("Veuillez corriger les erreurs avant de soumettre");
      return;
    }

    setLoading(true);
    setError("");
    setValidationErrors([]);
    setQuotaError({ show: false });

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        setLoading(false);
        return;
      }

      // Préparer le payload selon le type de tag
      let payload = {
        info_tag: formData.info_tag.trim(),
        statut_tag: parseInt(formData.statut_tag),
        type_tag: formData.type_tag
      };

      // Ajouter NFC UID seulement pour les tags NFC
      if (formData.type_tag === "NFC") {
        payload.nfc_uid = formData.nfc_uid.trim();
      }

      const response = await axios.post(`${API_BASE_URL}/tags/create_tags.php`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.success) {
        setSuccessMsg("Tag créé avec succès !");
        
        // Afficher le QR code généré automatiquement si c'est un tag QR
        if (formData.type_tag === "QR" && response.data?.data?.qr_code) {
          setSuccessMsg(prev => `${prev} QR code généré : ${response.data.data.qr_code}`);
        }
        
        setTimeout(() => {
          navigate("/tags");
        }, 2000);
      } else {
        if (response.data?.errors && Array.isArray(response.data.errors)) {
          setValidationErrors(response.data.errors);
        } else {
          setError(response.data?.message || "Erreur lors de la création du tag.");
        }
      }
    } catch (err) {
      if (err.response?.data) {
        // 🔍 Vérifier si c'est une erreur de quota
        if (err.response.data?.code === 'QUOTA_DEPASSE') {
          const data = err.response.data;
          setQuotaError({
            show: true,
            type: data.type,
            type_label: data.type_label || (formData.type_tag === 'QR' ? 'QR' : 'NFC'),
            message: data.message,
            utilises: data.utilises || 0,
            total: data.total || 0,
            restant: data.restant || 0
          });
        }
        // Vérifier si c'est une erreur d'abonnement
        else if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          setValidationErrors(err.response.data.errors);
        } else {
          setError(err.response.data.message || "Erreur lors de la création du tag.");
        }
      } else {
        setError("Erreur serveur : " + (err.message || "Erreur lors de la création du tag."));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      type_tag: "QR",
      info_tag: "",
      statut_tag: "1",
      nfc_uid: ""
    });
    setError("");
    setSuccessMsg("");
    setValidationErrors([]);
    setFieldErrors({});
    setQuotaError({ show: false });
  };

  const handleCancel = () => {
    navigate("/tags");
  };

  // Afficher un message si l'utilisateur n'est pas gestionnaire
  if (userInfo && String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Créer un Tag</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/tags">Tags</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Créer</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
              <p className="text-muted">Seuls les gestionnaires peuvent créer des tags.</p>
              <button
                className="btn mt-3 fw-semibold"
                onClick={() => navigate("/tags")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Créer un Tag</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/tags">Tags</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Nouveau Tag</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-tag-fill me-2" style={{ color: COLORS.secondary }}></i>
              Nouveau Tag
            </h5>

            {/* Quotas restants selon l'abonnement — informatif, affiché avant
                même de soumettre (le blocage réel se fait toujours côté
                backend, voir le message d'erreur de quota ci-dessous). Les deux
                types sont affichés, celui actuellement sélectionné est mis en
                évidence. */}
            {!quotaError.show && (quotaInfoQr || quotaInfoNfc) && (
              <div className="d-flex flex-wrap gap-2 mb-4">
                {[
                  { info: quotaInfoQr, label: 'Tags QR', type: 'tag_qr', icon: 'bi-qr-code', selected: formData.type_tag === 'QR' },
                  { info: quotaInfoNfc, label: 'Tags NFC', type: 'tag_nfc', icon: 'bi-wifi', selected: formData.type_tag === 'NFC' },
                ].filter(({ info }) => info).map(({ info, label, type, icon, selected }) => (
                  <div
                    key={type}
                    className="alert mb-0 flex-fill d-flex align-items-center justify-content-between flex-wrap gap-2"
                    style={{
                      minWidth: '260px',
                      backgroundColor: info.restant <= 0 ? '#ffebee' : (info.pourcentage >= 90 ? '#fff3e0' : '#E3F2FD'),
                      border: selected ? `2px solid ${COLORS.primary}` : 'none',
                      borderLeft: `4px solid ${info.restant <= 0 ? COLORS.danger : (info.pourcentage >= 90 ? COLORS.warning : COLORS.secondary)}`,
                      borderRadius: '8px'
                    }}
                  >
                    <div>
                      <i className={`bi ${icon} me-2`} style={{ color: COLORS.primary }}></i>
                      <strong>{info.utilises} sur {info.total}</strong> {label.toLowerCase()}
                      {' '}({info.restant > 0 ? `${info.restant} restant${info.restant > 1 ? 's' : ''}` : 'quota atteint'})
                    </div>
                    {info.pourcentage >= 90 && (
                      <a href={`/achat-quota?type=${type}`} className="btn btn-sm fw-semibold" style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '6px', textDecoration: 'none' }}>
                        <i className="bi bi-cart-plus me-1"></i> Acheter
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Message d'erreur de quota */}
            {quotaError.show && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {quotaError.message}
                    <div className="mt-3">
                      <div className="d-flex align-items-center justify-content-center mb-3">
                        <div className="text-center me-4">
                          <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                            {quotaError.utilises}
                          </div>
                          <small>Utilisés</small>
                        </div>
                        <div className="text-center me-4">
                          <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                            {quotaError.total}
                          </div>
                          <small>Total</small>
                        </div>
                        <div className="text-center">
                          <div className="fw-bold text-danger" style={{ fontSize: '1.2rem' }}>
                            {quotaError.restant}
                          </div>
                          <small>Restants</small>
                        </div>
                      </div>
                      
                      <div className="progress mb-3" style={{ height: '10px' }}>
                        <div 
                          className="progress-bar bg-warning" 
                          role="progressbar" 
                          style={{ 
                            width: `${(quotaError.utilises / quotaError.total) * 100}%` 
                          }}
                        ></div>
                      </div>

                      <div className="d-flex gap-2 justify-content-center">
                        <a 
                          href={`/achat-quota?type=${quotaError.type}`}
                          className="btn fw-semibold"
                          style={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 20px',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                        >
                          <i className="bi bi-cart-plus"></i>
                          Acheter plus de tags {quotaError.type_label}
                        </a>
                        <button
                          className="btn fw-semibold"
                          onClick={() => setQuotaError({ show: false })}
                          style={{
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 20px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                        >
                          Fermer
                        </button>
                      </div>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setQuotaError({ show: false })}
                  ></button>
                </div>
              </div>
            )}

            {/* Messages */}
            {successMsg && !quotaError.show && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <div className="flex-grow-1">
                    {successMsg}
                    <div className="mt-2">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Redirection en cours...
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccessMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {error && !quotaError.show && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
            )}

            {validationErrors.length > 0 && !quotaError.show && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff8e1',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.warning
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    <strong>Veuillez corriger les erreurs suivantes :</strong>
                    <ul className="mb-0 mt-2">
                      {validationErrors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit}>
              {/* Informations principales - PREMIÈRE LIGNE */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-info-circle"></i>
                    </div>
                    Informations principales
                  </h6>
                  
                  {/* PREMIÈRE LIGNE - 2 CHAMPS ALIGNÉS */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-qr-code-scan me-1" style={{ color: COLORS.secondary }}></i>
                          Type de Tag <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-gear"></i>
                          </span>
                          <select
                            className="form-select"
                            name="type_tag"
                            value={formData.type_tag}
                            onChange={handleChange}
                            disabled={loading}
                            required
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="QR">QR Code</option>
                            <option value="NFC">NFC</option>
                          </select>
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          {formData.type_tag === "QR" 
                            ? "Le QR code sera généré automatiquement" 
                            : "Vous devrez spécifier l'UID du tag NFC"}
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-toggle-on me-1" style={{ color: COLORS.secondary }}></i>
                          Statut <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-power"></i>
                          </span>
                          <select
                            className="form-select"
                            name="statut_tag"
                            value={formData.statut_tag}
                            onChange={handleChange}
                            disabled={loading}
                            required
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="1">Actif</option>
                            <option value="0">Inactif</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* DEUXIÈME LIGNE - 1 CHAMP PLEINE LARGEUR */}
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-card-text me-1" style={{ color: COLORS.secondary }}></i>
                          Informations du Tag <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary,
                            alignSelf: 'flex-start',
                            paddingTop: '12px'
                          }}>
                            <i className="bi bi-text-paragraph"></i>
                          </span>
                          <textarea
                            className={`form-control ${fieldErrors.info_tag ? "is-invalid" : ""}`}
                            name="info_tag"
                            value={formData.info_tag}
                            onChange={handleChange}
                            placeholder="Entrez les informations détaillées du tag..."
                            required
                            disabled={loading}
                            maxLength={1000}
                            rows="4"
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText,
                              resize: 'vertical'
                            }}
                          />
                        </div>
                        {fieldErrors.info_tag && (
                          <div className="text-danger mt-2 small d-flex align-items-center">
                            <i className="bi bi-exclamation-circle me-1"></i>
                            {fieldErrors.info_tag}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuration NFC - SEULEMENT POUR NFC */}
              {formData.type_tag === "NFC" && (
                <div className="card mb-4" style={{ 
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.lightBg}`,
                  boxShadow: 'none'
                }}>
                  <div className="card-body">
                    <h6 className="card-title mb-4" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600'
                    }}>
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#E8F5E9',
                        color: COLORS.success
                      }}>
                        <i className="bi bi-phone"></i>
                      </div>
                      Configuration NFC
                    </h6>
                    
                    {/* TROISIÈME LIGNE - NFC UID */}
                    <div className="row">
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-wifi me-1" style={{ color: COLORS.secondary }}></i>
                            NFC UID <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-wifi"></i>
                            </span>
                            <input
                              type="text"
                              className={`form-control ${fieldErrors.nfc_uid ? "is-invalid" : ""}`}
                              name="nfc_uid"
                              value={formData.nfc_uid}
                              onChange={handleChange}
                              disabled={loading}
                              required={formData.type_tag === "NFC"}
                              maxLength={64}
                              placeholder="Ex: A1B2C3D4E5F6"
                              style={{ 
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                          {fieldErrors.nfc_uid && (
                            <div className="text-danger mt-2 small d-flex align-items-center">
                              <i className="bi bi-exclamation-circle me-1"></i>
                              {fieldErrors.nfc_uid}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={loading}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleReset}
                    disabled={loading}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="btn fw-semibold"
                  disabled={loading || Object.keys(fieldErrors).length > 0 || !formData.info_tag.trim() || 
                    (formData.type_tag === "NFC" && !formData.nfc_uid.trim())}
                  style={{
                    color: 'white',
                    backgroundColor: loading || Object.keys(fieldErrors).length > 0 || !formData.info_tag.trim() || 
                      (formData.type_tag === "NFC" && !formData.nfc_uid.trim()) ? '#B0BEC5' : COLORS.primary,
                    borderColor: loading || Object.keys(fieldErrors).length > 0 || !formData.info_tag.trim() || 
                      (formData.type_tag === "NFC" && !formData.nfc_uid.trim()) ? '#B0BEC5' : COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    opacity: loading ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && Object.keys(fieldErrors).length === 0 && formData.info_tag.trim() && 
                      (formData.type_tag !== "NFC" || formData.nfc_uid.trim())) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && Object.keys(fieldErrors).length === 0 && formData.info_tag.trim() && 
                      (formData.type_tag !== "NFC" || formData.nfc_uid.trim())) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Créer le tag
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          background-color: ${COLORS.lightBg};
          border-color: #dee2e6;
        }
        
        .is-invalid {
          border-color: ${COLORS.danger} !important;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-md-4 {
            margin-bottom: 1rem;
          }
          
          .col-md-4 textarea.form-control {
            min-height: 120px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default CreateTag;